import { useEffect, useState } from 'react'
import {
  MessageSquare,
  Send,
  Tag,
  Settings2,
  StickyNote,
  Ban,
  ShieldCheck,
  ShieldX,
  Workflow,
  Megaphone,
  CircleDot,
} from 'lucide-react'
import { api } from '@/lib/api'

interface Activity {
  id: string
  type: string
  sourceModule: string
  sourceId: string | null
  payloadJson: Record<string, unknown> | null
  occurredAt: string
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  MESSAGE_INBOUND: MessageSquare,
  MESSAGE_OUTBOUND: Send,
  TAG_ADDED: Tag,
  TAG_REMOVED: Tag,
  CUSTOM_FIELD_CHANGED: Settings2,
  CUSTOM_FIELD_CLEARED: Settings2,
  NOTE_ADDED: StickyNote,
  NOTE_REMOVED: StickyNote,
  CONSENT_GIVEN: ShieldCheck,
  CONSENT_WITHDRAWN: ShieldX,
  OPT_OUT: Ban,
  DND_CHANGED: Ban,
  CAMPAIGN_ENROLLED: Megaphone,
  WORKFLOW_ENROLLED: Workflow,
}

function describe(a: Activity): string {
  const p = (a.payloadJson ?? {}) as Record<string, unknown>
  switch (a.type) {
    case 'MESSAGE_INBOUND':
      return `Mensaje recibido por ${p.channel ?? '?'} · "${truncate(p.preview as string)}"`
    case 'MESSAGE_OUTBOUND':
      return `Mensaje enviado por ${p.channel ?? '?'} · "${truncate(p.preview as string)}" · ${p.providerStatus ?? ''}`
    case 'TAG_ADDED':
      return `Tag añadido: ${p.tagName ?? a.sourceId} · ${p.appliedBy ?? 'USER'}`
    case 'TAG_REMOVED':
      return `Tag quitado: ${p.tagName ?? a.sourceId}`
    case 'CUSTOM_FIELD_CHANGED':
      return `${p.fieldName ?? a.sourceId} = ${customFieldValuePreview(p)}`
    case 'CUSTOM_FIELD_CLEARED':
      return `${p.fieldName ?? a.sourceId} borrado`
    case 'NOTE_ADDED':
      return `Nota interna · "${truncate(p.preview as string)}"`
    case 'CONSENT_GIVEN':
      return `Consent dado para ${p.channel ?? '?'}`
    case 'CONSENT_WITHDRAWN':
      return `Consent retirado para ${p.channel ?? '?'}`
    case 'OPT_OUT':
      return `Opt-out · ${p.channel ?? '?'} · ${p.keyword ?? ''}`
    case 'CAMPAIGN_ENROLLED':
      return `Inscrito en campaña ${p.campaignName ?? a.sourceId}`
    case 'WORKFLOW_ENROLLED':
      return `Inscrito en workflow ${p.workflowName ?? a.sourceId}`
    default:
      return a.type
  }
}

function customFieldValuePreview(p: Record<string, unknown>): string {
  if (p.valueText !== undefined && p.valueText !== null) return String(p.valueText)
  if (p.valueNumber !== undefined && p.valueNumber !== null) return String(p.valueNumber)
  if (p.valueBool !== undefined && p.valueBool !== null) return (p.valueBool as boolean) ? 'Sí' : 'No'
  if (p.valueDate) return new Date(p.valueDate as string).toLocaleDateString('es-ES')
  return '—'
}

function truncate(s: string | null | undefined, max = 80): string {
  if (!s) return ''
  return s.length > max ? s.slice(0, max) + '…' : s
}

interface Props {
  clientStubId: string
}

export function ActivityTimeline({ clientStubId }: Props) {
  const [items, setItems] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api<{ items: Activity[] }>(`/v1/marketing/clients/${clientStubId}/activity?limit=100`)
      .then((r) => setItems(r.items))
      .catch(() => undefined)
      .finally(() => setLoading(false))
  }, [clientStubId])

  if (loading) return <div className="text-xs font-mono text-ink-700/50">Cargando…</div>
  if (items.length === 0)
    return (
      <div className="text-xs font-mono text-ink-700/50 text-center py-6">
        Sin actividad registrada todavía.
      </div>
    )

  return (
    <ul className="space-y-2">
      {items.map((a) => {
        const Icon = ICONS[a.type] ?? CircleDot
        return (
          <li key={a.id} className="flex gap-2">
            <div className="shrink-0 mt-0.5 w-6 h-6 rounded border border-ink-900/10 bg-white flex items-center justify-center">
              <Icon className="size-3 text-blueprint-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-ink-900 break-words">{describe(a)}</div>
              <div className="text-[10px] font-mono text-ink-700/50 flex items-center gap-1.5">
                <span>{new Date(a.occurredAt).toLocaleString('es-ES')}</span>
                <span className="bp-pill">{a.sourceModule}</span>
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
